// SAB-600 follow-up: shared "gather a geo session for a server-gated money action"
// helper. Extracted so the join and buy surfaces stop duplicating (and mis-gating)
// the logic the deposit helpers already run inline (WalletDeposit acquireGeoSession,
// NFLFundsSection acquireGeoSession).
//
// FLAG SEMANTICS (this is the gap that caused the missing_session outage):
//   GEO_ENABLED controls the client-side BLOCKING UI (fail-close + GeoBlockScreen).
//   GEO_OBSERVE controls whether the client GATHERS and sends a session.
// A surface that only checked GEO_ENABLED never gathered under the prod combination
// (GEO_ENABLED false, GEO_OBSERVE true, server GEO_ENFORCE true), so it sent no
// session and the server fail-closed every request with missing_session. Gather must
// gate on GEO_ENABLED || GEO_OBSERVE.
import { GEO_ENABLED, GEO_OBSERVE } from './locance';
import { pingBeforeMoneyActionIfStale } from './devicePing';

// Minimal shape of the useLocanceGeoCheck runner. Kept local (not imported from the
// hook) to avoid a circular import with locance.ts.
interface GeoRunner {
  run: () => Promise<{ result?: string; sessionId?: string } | null>;
}

export interface AcquiredGeoSession {
  // false ONLY when the client should fail-close LOCALLY (enforce client on a
  // non-Compliant verdict). The caller applies its own block treatment on false
  // (return + geo.geoBlock drives GeoBlockScreen, or a toast). ok:true otherwise.
  ok: boolean;
  // Fresh Locance session id to forward as geo_session_id, when one was gathered.
  sessionId?: string;
}

/**
 * Gather a fresh Locance geo session for a server-gated money action.
 *
 * Mirrors the deposit helpers: gathers under GEO_ENABLED OR GEO_OBSERVE, fail-closes
 * locally ONLY under GEO_ENABLED (enforce client) on a non-Compliant verdict, and
 * under observe-only sends the session and lets the SERVER decide. It renders no UI.
 *
 * If geo.run() throws or yields no session, it returns ok:true with no session so the
 * caller sends nothing and the server fails closed. The error is logged, never
 * swallowed silently.
 */
export async function acquireGeoSession(geo: GeoRunner, leagueId?: string | null): Promise<AcquiredGeoSession> {
  // Geo entirely off (neither enforce nor observe): unchanged pre-gate flow.
  if (!GEO_ENABLED && !GEO_OBSERVE) return { ok: true };

  let verdict: { result?: string; sessionId?: string } | null;
  try {
    verdict = await geo.run();
  } catch (err) {
    console.error('[geo] session gather threw; sending no session, server will fail closed', err);
    return { ok: true };
  }

  // SAB item 5: geo.run() above ran the collector, refreshing the cached GPS fix. Fire a
  // Soap device-ping if the last one is stale (>30 min). Telemetry, non-blocking, never
  // prompts (skips if no recent cached position).
  pingBeforeMoneyActionIfStale(leagueId);

  // Observe-only (GEO_ENABLED off, GEO_OBSERVE on): send the session, never fail-close.
  if (!GEO_ENABLED) {
    if (!verdict?.sessionId) {
      console.warn('[geo] gather returned no session id; sending nothing, server will fail closed', {
        result: verdict?.result ?? null,
      });
    }
    return { ok: true, sessionId: verdict?.sessionId };
  }

  // Enforce client (GEO_ENABLED on): fail-close locally only on a non-Compliant verdict.
  if (verdict?.result === 'Compliant') return { ok: true, sessionId: verdict.sessionId };
  return { ok: false };
}
